import React, { useRef, useEffect, useState } from 'react';
import { 
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Link, Image as ImageIcon, Table, Code, X,
  Undo, Redo
} from 'lucide-react';
import { API_BASE_URL } from '../config/api';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
  value, 
  onChange, 
  disabled = false,
  placeholder = 'Write your trading notes here...'
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Link & Table Modals states
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);

  const savedRangeRef = useRef<Range | null>(null);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Handle content change
  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      onChange(html);
    }
  };

  // Execute formatting command
  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  // Selection preservation helpers to prevent losing cursor position when clicking modal inputs
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range;
      } else {
        savedRangeRef.current = null;
      }
    } else {
      savedRangeRef.current = null;
    }
  };

  const restoreSelection = () => {
    if (savedRangeRef.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
      }
    }
  };

  // Handle image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      alert('Authentication required');
      return;
    }

    const fd = new FormData();
    fd.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await response.json();
      if (response.ok) {
        insertImage(data.url);
      } else {
        alert(data.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error(err);
      alert('Error connecting to upload server');
    }
  };

  // Insert image into editor
  const insertImage = (src: string) => {
    const img = document.createElement('img');
    img.src = src;
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.borderRadius = '8px';
    img.style.display = 'block';
    
    const selection = window.getSelection();
    let inserted = false;
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      // Ensure the cursor/selection is inside the editor container
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        range.insertNode(img);
        
        // Move cursor after image
        range.setStartAfter(img);
        range.setEndAfter(img);
        selection.removeAllRanges();
        selection.addRange(range);
        inserted = true;
      }
    }
    
    if (!inserted) {
      editorRef.current?.appendChild(img);
      editorRef.current?.focus();
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(editorRef.current!);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    handleInput();
  };

  // Modal triggers
  const openLinkModal = () => {
    saveSelection();
    setLinkUrl('');
    setShowLinkModal(true);
  };

  const handleLinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowLinkModal(false);
    restoreSelection();
    if (linkUrl) {
      if (savedRangeRef.current) {
        execCommand('createLink', linkUrl);
      } else {
        editorRef.current?.focus();
        const selection = window.getSelection();
        if (selection) {
          const range = document.createRange();
          range.selectNodeContents(editorRef.current!);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        execCommand('createLink', linkUrl);
      }
    }
  };

  const openTableModal = () => {
    saveSelection();
    setTableRows(3);
    setTableCols(3);
    setShowTableModal(true);
  };

  const handleTableSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowTableModal(false);
    restoreSelection();
    
    if (tableRows > 0 && tableCols > 0) {
      let tableHTML = '<table style="border-collapse: collapse; width: 100%; margin: 16px 0;">';
      for (let i = 0; i < tableRows; i++) {
        tableHTML += '<tr>';
        for (let j = 0; j < tableCols; j++) {
          tableHTML += '<td style="border: 1px solid #404040; padding: 8px; min-width: 50px;">&nbsp;</td>';
        }
        tableHTML += '</tr>';
      }
      tableHTML += '</table>';
      
      if (savedRangeRef.current) {
        execCommand('insertHTML', tableHTML);
      } else {
        editorRef.current?.focus();
        const selection = window.getSelection();
        if (selection) {
          const range = document.createRange();
          range.selectNodeContents(editorRef.current!);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        execCommand('insertHTML', tableHTML);
      }
    }
  };

  // Toolbar button component
  const ToolbarButton = ({ 
    icon, 
    command, 
    value, 
    title,
    onClick 
  }: { 
    icon: React.ReactNode; 
    command?: string; 
    value?: string;
    title: string;
    onClick?: () => void;
  }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        if (disabled) return;
        e.preventDefault();
        if (onClick) {
          onClick();
        } else if (command) {
          execCommand(command, value);
        }
      }}
      disabled={disabled}
      title={title}
      className="min-w-[34px] min-h-[34px] p-1.5 hover:bg-neutral-800 rounded-md text-neutral-500 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
    >
      {icon}
    </button>
  );

  return (
    <div className="w-full">
      {/* Toolbar */}
      {!disabled && (
        <div className="flex flex-wrap items-center gap-0.5 mb-3 p-1 bg-neutral-900/50 rounded-lg border border-neutral-800 sticky top-0 backdrop-blur-md z-10 overflow-x-auto">
          {/* Text Formatting */}
          <div className="flex items-center gap-0.5">
            <ToolbarButton 
              icon={<Bold size={15} />} 
              command="bold" 
              title="Bold (Ctrl+B)"
            />
            <ToolbarButton 
              icon={<Italic size={15} />} 
              command="italic" 
              title="Italic (Ctrl+I)"
            />
            <ToolbarButton 
              icon={<Underline size={15} />} 
              command="underline" 
              title="Underline (Ctrl+U)"
            />
          </div>

          <div className="hidden sm:block w-px h-5 bg-neutral-800 mx-0.5" />

          {/* Alignment */}
          <div className="flex items-center gap-0.5">
            <ToolbarButton 
              icon={<AlignLeft size={15} />} 
              command="justifyLeft" 
              title="Align Left"
            />
            <ToolbarButton 
              icon={<AlignCenter size={15} />} 
              command="justifyCenter" 
              title="Align Center"
            />
            <ToolbarButton 
              icon={<AlignRight size={15} />} 
              command="justifyRight" 
              title="Align Right"
            />
          </div>

          <div className="hidden sm:block w-px h-5 bg-neutral-800 mx-0.5" />

          {/* Lists & Insert */}
          <div className="flex items-center gap-0.5 flex-wrap">
            <ToolbarButton 
              icon={<List size={15} />} 
              command="insertUnorderedList" 
              title="Bullet List"
            />
            <ToolbarButton 
              icon={<ListOrdered size={15} />} 
              command="insertOrderedList" 
              title="Numbered List"
            />
            <ToolbarButton 
              icon={<Link size={15} />} 
              onClick={openLinkModal}
              title="Insert Link"
            />
            <ToolbarButton 
              icon={<ImageIcon size={15} />} 
              onClick={() => fileInputRef.current?.click()}
              title="Insert Image"
            />
            <ToolbarButton 
              icon={<Table size={15} />} 
              onClick={openTableModal}
              title="Insert Table"
            />
            <ToolbarButton 
              icon={<Code size={15} />} 
              command="formatBlock"
              value="pre"
              title="Code Block"
            />
          </div>

          <div className="flex-1 min-w-[8px]" />

          {/* Undo / Redo */}
          <div className="flex items-center gap-0.5">
            <ToolbarButton 
              icon={<Undo size={15} />} 
              command="undo" 
              title="Undo (Ctrl+Z)"
            />
            <ToolbarButton 
              icon={<Redo size={15} />} 
              command="redo" 
              title="Redo (Ctrl+Y)"
            />
          </div>
        </div>
      )}

      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`
          min-h-[240px] 
          w-full 
          outline-none 
          text-sm 
          leading-relaxed 
          prose 
          prose-invert 
          max-w-none
          ${disabled ? 'text-neutral-400 cursor-not-allowed' : 'text-neutral-300'}
          ${isFocused ? 'ring-1 ring-red-600/20 rounded-lg' : ''}
        `}
        style={{
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
        data-placeholder={placeholder}
      />

      {/* Custom Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form 
            onSubmit={handleLinkSubmit}
            className="bg-[#0b0b0b] border border-neutral-850 rounded-2xl p-4 sm:p-6 w-full max-w-full sm:max-w-sm space-y-4 shadow-2xl animate-fade-in"
          >
            <div className="flex justify-between items-center">
              <span className="text-xs font-black uppercase text-neutral-400 tracking-wider">Insert Link</span>
              <button 
                type="button" 
                onClick={() => setShowLinkModal(false)}
                className="text-neutral-500 hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-1">
              <label className="text-[9px] font-black text-neutral-500 block">URL ADDRESS</label>
              <input
                type="url"
                required
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-red-500 w-full min-h-[48px]"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="bg-neutral-900 hover:bg-neutral-850 text-neutral-400 font-bold text-xs py-3 px-4 rounded-lg w-full border border-neutral-800 transition-all min-h-[48px]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs py-3 px-4 rounded-lg w-full transition-all min-h-[48px]"
              >
                Insert
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Custom Table Modal */}
      {showTableModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form 
            onSubmit={handleTableSubmit}
            className="bg-[#0b0b0b] border border-neutral-850 rounded-2xl p-4 sm:p-6 w-full max-w-full sm:max-w-sm space-y-4 shadow-2xl animate-fade-in"
          >
            <div className="flex justify-between items-center">
              <span className="text-xs font-black uppercase text-neutral-400 tracking-wider">Insert Table</span>
              <button 
                type="button" 
                onClick={() => setShowTableModal(false)}
                className="text-neutral-500 hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[9px] font-black text-neutral-500 block">ROWS</label>
                <input
                  type="number"
                  min="1"
                  max="15"
                  required
                  value={tableRows}
                  onChange={e => setTableRows(parseInt(e.target.value) || 1)}
                  className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-red-500 w-full min-h-[48px]"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black text-neutral-500 block">COLUMNS</label>
                <input
                  type="number"
                  min="1"
                  max="15"
                  required
                  value={tableCols}
                  onChange={e => setTableCols(parseInt(e.target.value) || 1)}
                  className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-red-500 w-full min-h-[48px]"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowTableModal(false)}
                className="bg-neutral-900 hover:bg-neutral-850 text-neutral-400 font-bold text-xs py-3 px-4 rounded-lg w-full border border-neutral-800 transition-all min-h-[48px]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs py-3 px-4 rounded-lg w-full transition-all min-h-[48px]"
              >
                Insert
              </button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #525252;
          pointer-events: none;
          position: absolute;
        }
        
        [contenteditable] {
          padding: 12px;
        }

        [contenteditable] img {
          max-width: 100%;
          height: auto;
          display: block;
          border-radius: 8px;
        }

        [contenteditable] table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
        }

        [contenteditable] table td {
          border: 1px solid #404040;
          padding: 8px;
          min-width: 50px;
        }

        [contenteditable] pre {
          background: #1a1a1a;
          border: 1px solid #404040;
          border-radius: 8px;
          padding: 12px;
          overflow-x: auto;
          font-family: 'Courier New', monospace;
          font-size: 14px;
        }

        [contenteditable] a {
          color: #ef4444;
          text-decoration: underline;
        }

        [contenteditable] ul, [contenteditable] ol {
          padding-left: 24px;
          margin: 12px 0;
        }

        [contenteditable] li {
          margin: 4px 0;
        }

        [contenteditable] strong {
          font-weight: bold;
        }

        [contenteditable] em {
          font-style: italic;
        }

        [contenteditable] u {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
